/**
 * B-652 — wspólne elementy zakładki „Instruktorzy”: zapytania, formatowanie czasu
 * (zawsze Europe/Warsaw — panel bywa otwierany z komputerów w innej strefie) i drobne
 * znaczniki poziomu zaległości.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/shared/lib/api';
import { cn } from '@/shared/lib/utils';
import type {
  InstructorsBoard, OverdueCounts, InstructorDay, OversightSettings, OversightSettingsPatch,
  TimelinessRow, OverdueItem, OverdueLevel, RuleKey, InstructorRow, FormCounts, ControlCounts, RowHint,
} from './types';

// ─── Zapytania ──────────────────────────────────────────────────────────────

export const KLUCZ_INSTRUKTOROW = ['duty-log', 'instructors'] as const;

export function useInstructorsBoard() {
  return useQuery<InstructorsBoard>({
    queryKey: [...KLUCZ_INSTRUKTOROW, 'board'],
    queryFn: () => api.get<InstructorsBoard>('/duty-log/instructors'),
    refetchInterval: 30_000,
  });
}

/**
 * Lekki licznik (zakładka i menu boczne). Błąd (np. 403 u roli bez dostępu albo stary
 * backend bez tej trasy) daje `null`, a nie wyjątek — znaczek po prostu się nie pokazuje.
 */
export function useOverdueCounts(enabled: boolean) {
  return useQuery<OverdueCounts | null>({
    queryKey: [...KLUCZ_INSTRUKTOROW, 'summary'],
    queryFn: async () => {
      try {
        const r = await api.get<OverdueCounts>('/duty-log/instructors/summary');
        return { overdue: Number(r?.overdue) || 0, urgent: Number(r?.urgent) || 0 };
      } catch { return null; }
    },
    enabled,
    refetchInterval: 60_000,
    staleTime: 20_000,
  });
}

export function useInstructorDay(userId: string | null) {
  return useQuery<InstructorDay>({
    queryKey: [...KLUCZ_INSTRUKTOROW, 'day', userId],
    queryFn: () => api.get<InstructorDay>(`/duty-log/instructors/${userId}/day`),
    enabled: !!userId,
    refetchInterval: 30_000,
  });
}

/** „Przypomnij” — bez `itemKeys` serwer bierze wszystkie bieżące zaległości osoby. */
export function useRemind() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, itemKeys }: { userId: string; itemKeys?: string[] }) =>
      api.post<{ sent: number }>(`/duty-log/instructors/${userId}/remind`, itemKeys?.length ? { itemKeys } : {}),
    onSuccess: (r) => {
      const n = Number(r?.sent) || 0;
      toast.success(n > 0 ? `Wysłano przypomnienie (${n} ${n === 1 ? 'pozycja' : n < 5 ? 'pozycje' : 'pozycji'})` : 'Brak bieżących zaległości do przypomnienia');
      qc.invalidateQueries({ queryKey: [...KLUCZ_INSTRUKTOROW] });
    },
    onError: () => toast.error('Nie udało się wysłać przypomnienia'),
  });
}

/** 28.10: wyjaśnienie czynności bez wymaganego formularza (gasi R12); pusty tekst usuwa wyjaśnienie. */
export function useNoFormReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ activityId, reason }: { activityId: string; reason: string }) =>
      api.post<{ ok: true; noFormReason: string | null }>(`/duty-log/instructors/activities/${activityId}/no-form-reason`, { reason }),
    onSuccess: (r) => {
      toast.success(r?.noFormReason ? 'Zapisano wyjaśnienie' : 'Usunięto wyjaśnienie');
      qc.invalidateQueries({ queryKey: [...KLUCZ_INSTRUKTOROW] });
    },
    onError: () => toast.error('Nie udało się zapisać wyjaśnienia'),
  });
}

export function useOversightSettings(enabled = true) {
  return useQuery<OversightSettings>({
    queryKey: ['duty-log', 'oversight-settings'],
    queryFn: () => api.get<OversightSettings>('/duty-log/oversight-settings'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSaveOversightSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: OversightSettingsPatch) => api.put<OversightSettings>('/duty-log/oversight-settings', patch),
    onSuccess: (s) => {
      qc.setQueryData(['duty-log', 'oversight-settings'], s);
      qc.invalidateQueries({ queryKey: [...KLUCZ_INSTRUKTOROW] });
      toast.success('Zapisano progi zaległości');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ? String(e.response.data.message) : 'Nie udało się zapisać progów'),
  });
}

export function useTimeliness(from: string, to: string) {
  return useQuery<{ rows: TimelinessRow[] }>({
    queryKey: ['duty-log', 'stats', 'instructor-timeliness', from, to],
    queryFn: () => api.get<{ rows: TimelinessRow[] }>('/duty-log/stats/instructor-timeliness', { from, to }),
  });
}

// ─── Reguły ─────────────────────────────────────────────────────────────────

export const RULE_KEYS: RuleKey[] = ['R1', 'R2', 'R3', 'R4', 'R5', 'R6', 'R7', 'R8', 'R9', 'R10', 'R11', 'R12', 'R13'];

export const RULE_CFG: Record<RuleKey, { title: string; what: string }> = {
  R1:  { title: 'Dyspozycja nieprzyjęta',              what: 'zadanie z dyspozycji wciąż oczekuje' },
  R2:  { title: 'Dyspozycja zakończona bez dokumentu', what: 'czynność z dyspozycji zakończona, brak formularza' },
  R3:  { title: 'Kolizja bez oświadczenia',            what: 'obcy uczestnik bez wystawionego oświadczenia' },
  R4:  { title: 'Szkic niewysłany',                    what: 'formularz w szkicu na serwerze' },
  R5:  { title: 'Zwrot do poprawy bez reakcji',        what: 'formularz zwrócony, brak ponownego wysłania' },
  R6:  { title: 'Zadanie z planu pominięte',           what: 'zadanie oczekuje po planowanej godzinie' },
  R7:  { title: 'Brak rozpoczęcia służby',             what: 'jest plan, brak odbicia i czynności' },
  R8:  { title: 'Dzień niezamknięty',                  what: 'liczone jak przypomnienia czasu pracy' },
  R9:  { title: 'Monitoring wymagany, niezlecony',     what: 'formularz z wymaganym monitoringiem bez wniosku' },
  R10: { title: 'Brak lub stara pozycja w czynności',  what: 'nie liczy się do zaległości — tylko kolor na mapie' },
  R11: { title: 'Szkic tylko w telefonie',             what: 'z aplikacji: szkic zapisany lokalnie' },
  R12: { title: 'Czynność bez wymaganego formularza',  what: 'definicja „Wymaga formularza”, czynność zakończona bez dokumentu i bez wyjaśnienia' },
  R13: { title: 'Sprawa czeka na drugi formularz',     what: 'dokument wysłany, jego powiązany wciąż w szkicu (1 z 2)' },
};

// ─── Czas (Europe/Warsaw) ───────────────────────────────────────────────────

const TZ = 'Europe/Warsaw';
const fHHMM = new Intl.DateTimeFormat('pl-PL', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
const fDzien = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
const fDDMM = new Intl.DateTimeFormat('pl-PL', { timeZone: TZ, day: '2-digit', month: '2-digit' });
const fNaglowek = new Intl.DateTimeFormat('pl-PL', { timeZone: TZ, weekday: 'short', day: '2-digit', month: '2-digit' });
const fGodzMin = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

const poprawna = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** „14:22” w strefie Warszawy. */
export function hhmm(iso?: string | null): string {
  const d = poprawna(iso);
  return d ? fHHMM.format(d) : '—';
}

/** Data warszawska „YYYY-MM-DD”. */
export function dzienWaw(d: Date): string {
  return fDzien.format(d);
}

/** Dziś: „11:40”; inny dzień: „19.10 12:10”. */
export function kiedy(iso?: string | null): string {
  const d = poprawna(iso);
  if (!d) return '—';
  return dzienWaw(d) === dzienWaw(new Date()) ? fHHMM.format(d) : `${fDDMM.format(d)} ${fHHMM.format(d)}`;
}

/** „wt. 20.10” */
export function naglowekDnia(d: Date): string {
  return fNaglowek.format(d);
}

/** Minuty od północy (Warszawa) — do osi dnia. */
export function minutyDnia(iso: string): number {
  const d = poprawna(iso);
  if (!d) return 0;
  const [h, m] = fGodzMin.format(d).split(':').map(Number);
  return h * 60 + m;
}

/** „08:00–16:00” */
export function przedzial(a?: string | null, b?: string | null): string {
  return `${hhmm(a)}–${hhmm(b)}`;
}

/** Wiek zaległości: „18 min”, „2 h 42”, „26 h”, „4 dni”. */
export function wiek(min: number | null | undefined): string {
  if (min == null || !Number.isFinite(min)) return '—';
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h} h ${String(m % 60).padStart(2, '0')}` : `${h} h`;
  if (h < 72) return `${h} h`;
  const d = Math.floor(h / 24);
  return `${d} dni`;
}

/** Próg w minutach czytelnie: 240 → „4 h”, 90 → „90 min”, 150 → „2 h 30 min”. */
export function prog(min: number | null | undefined): string {
  if (min == null) return '—';
  if (min < 120 && min % 60 !== 0) return `${min} min`;
  const h = Math.floor(min / 60);
  return min % 60 ? `${h} h ${min % 60} min` : `${h} h`;
}

/** „M. Zielińska” */
export function krotko(u: { firstName: string; lastName: string }): string {
  return `${u.firstName ? `${u.firstName[0]}. ` : ''}${u.lastName}`;
}

export function inicjaly(u: { firstName: string; lastName: string }): string {
  return `${u.firstName?.[0] ?? ''}${u.lastName?.[0] ?? ''}`.toUpperCase() || '?';
}

// ─── Poziomy ────────────────────────────────────────────────────────────────

export const LEVEL_CFG: Record<OverdueLevel, { label: string; pill: string; dot: string; hex: string; soft: string }> = {
  URGENT: { label: 'pilne',   pill: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',         dot: 'bg-red-500',   hex: '#dc2626', soft: 'border-red-200 bg-red-50/70 dark:border-red-500/30 dark:bg-red-500/10' },
  WARN:   { label: 'zaległe', pill: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300', dot: 'bg-amber-500', hex: '#d97706', soft: 'border-amber-200 bg-amber-50/70 dark:border-amber-500/30 dark:bg-amber-500/10' },
};

/** Najgorszy poziom z listy (null = w porządku). */
export function najgorszy(items: { level: OverdueLevel }[]): OverdueLevel | null {
  if (items.some((i) => i.level === 'URGENT')) return 'URGENT';
  return items.length ? 'WARN' : null;
}

export function LevelPill({ level, className }: { level: OverdueLevel; className?: string }) {
  const c = LEVEL_CFG[level];
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold whitespace-nowrap', c.pill, className)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', c.dot)} />{c.label}
    </span>
  );
}

/** Pastylka „Kolizja bez oświadczenia · 2 h 42”. */
export function OverduePill({ item, className }: { item: OverdueItem; className?: string }) {
  const c = LEVEL_CFG[item.level];
  return (
    <span title={item.detail} className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold whitespace-nowrap', c.pill, className)}>
      {item.title}<span className="opacity-70 tabular-nums">· {wiek(item.ageMin)}</span>
    </span>
  );
}

/** Kolor awatara wg najgorszej zaległości. */
export function kolorAwatara(row: InstructorRow): string {
  const l = najgorszy(row.overdue);
  if (l === 'URGENT') return 'bg-red-500 text-white';
  if (l === 'WARN') return 'bg-amber-500 text-white';
  return row.onDuty ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground';
}

export const FORM_STATUS: Record<string, { label: string; cls: string }> = {
  DRAFT:     { label: 'szkic',        cls: 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300' },
  SAVED:     { label: 'szkic',        cls: 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300' },
  SUBMITTED: { label: 'wysłany',      cls: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300' },
  RETURNED:  { label: 'zwrócony',     cls: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300' },
  APPROVED:  { label: 'zatwierdzony', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' },
  REALIZED:  { label: 'zrealizowany', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' },
  CLOSED:    { label: 'zamknięty',    cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' },
  CANCELLED: { label: 'anulowany',    cls: 'bg-muted text-muted-foreground' },
};

// ─── 28.10: formularze i kontrole instruktorów ───────────────────────────────

const PILL = 'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold whitespace-nowrap tabular-nums';

/** „2 wysłane · 1 szkic · 1 zwrot · 1 w telefonie · sprawa 1 z 2” — zero formularzy jako szary znacznik. */
export function LicznikiFormularzy({ forms, className }: { forms: FormCounts | undefined; className?: string }) {
  if (!forms) return null;
  const pl = (n: number, one: string, few: string, many: string) =>
    `${n} ${n === 1 ? one : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? few : many}`;
  if (forms.total === 0 && forms.offline === 0) {
    return <span className={cn(PILL, 'bg-muted text-muted-foreground', className)}>0 formularzy</span>;
  }
  return (
    <span className={cn('flex flex-wrap gap-1', className)}>
      {forms.sent > 0 && <span className={cn(PILL, 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300')}>{pl(forms.sent, 'wysłany', 'wysłane', 'wysłanych')}</span>}
      {forms.draft > 0 && <span className={cn(PILL, 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300')}>{pl(forms.draft, 'szkic', 'szkice', 'szkiców')}</span>}
      {forms.returned > 0 && <span className={cn(PILL, 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300')}>{pl(forms.returned, 'zwrot', 'zwroty', 'zwrotów')}</span>}
      {forms.offline > 0 && <span className={cn(PILL, 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300')} title="Szkice zapisane tylko w telefonie">{forms.offline} w telefonie</span>}
      {forms.linkedPending > 0 && <span className={cn(PILL, 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300')} title="Wysłany dokument, którego powiązany czeka w szkicu">{forms.linkedPending === 1 ? 'sprawa 1 z 2' : `${forms.linkedPending} sprawy 1 z 2`}</span>}
    </span>
  );
}

/** „trzeźw. 9” (+ „1 alarm” tylko dla roli z danymi o zdrowiu). */
export function Kontrole({ controls }: { controls: ControlCounts | undefined }) {
  if (!controls) return null;
  const n = controls.sobriety;
  return (
    <span className="flex flex-wrap gap-1">
      <span title="Badania trzeźwości (osoby) w dzisiejszych dokumentach"
        className={cn('inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold tabular-nums whitespace-nowrap',
          n > 0 ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300' : 'bg-muted text-muted-foreground')}>
        trzeźw. {n}
      </span>
      {(controls.sobrietyAlarm ?? 0) > 0 && (
        <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300 whitespace-nowrap">
          {controls.sobrietyAlarm} alarm
        </span>
      )}
    </span>
  );
}

/** Jedna linijka pod licznikami: ostrzeżenie (czerwone), podpowiedź (szara) albo nic przy „w porządku”. */
export function Podpowiedz({ hint, className }: { hint: RowHint | undefined; className?: string }) {
  if (!hint || hint.level === 'OK') return null;
  return (
    <p className={cn('text-[10.5px] leading-snug', className)}>
      <span className={hint.level === 'WARN' ? 'font-semibold text-red-600 dark:text-red-400' : 'text-muted-foreground'}>{hint.text}</span>
      {hint.detail && <span className="block text-muted-foreground">{hint.detail}</span>}
    </p>
  );
}

/** „1 z 2” przy dokumencie, który należy do niekompletnej sprawy. */
export function SprawaPill({ p }: { p: { wyslane: number; razem: number; niekompletna: boolean } | null | undefined }) {
  if (!p) return null;
  return (
    <span title={p.niekompletna ? 'Sprawa czeka na powiązany formularz' : 'Wszystkie dokumenty sprawy wysłane'}
      className={cn(PILL, p.niekompletna ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300' : 'bg-muted text-muted-foreground')}>
      {p.wyslane} z {p.razem}
    </span>
  );
}
